// `marketing.plan-changed.v1` — emitted by pricing-backend when an org's plan
// changes AND that change flips its SMS eligibility, for an org that has already
// entered Marketing (has a pocketflow_business_id). It closes the gap the
// PE-1679 comment named ("Ideal future trigger: a dedicated plan-change
// webhook"): the PocketFlows embed gates the SMS step off the PF business's
// `disabled_steps`, which our backend only wrote on marketing-enable and
// SMS-number-request — so a BASE→PLUS upgrade left the SMS step hidden.
//
// IT IS A TRANSITION EVENT, NOT A PLAN-VALUE EVENT (same discipline as
// marketing.subscription.ended.v1). Pricing emits it ONLY when SMS eligibility
// CROSSES — BASE (no SMS) → PLUS/PRO (SMS), or PLUS/PRO → BASE — for an org with
// a PF business. It does NOT emit on a same-eligibility write (PLUS→PRO, a
// cancel-toggle `updated`, or a redelivered already-applied event whose prior
// plan already equals the new one). Checking the eligibility transition keeps
// the emit idempotent against Stripe webhook redelivery.
//
// Producer: pricing-backend (`Source: 'charitystack.pricing'`), from the
//   verified Stripe webhook grant path, via the `PocketflowStepSyncPort` seam
//   (record-subscription.ts). The emit MUST throw on EventBridge
//   `FailedEntryCount > 0` so the webhook 500s and Stripe redelivers — the
//   pricing-side plan write is idempotent and safe to repeat.
// Consumer: marketing-backend — the `resync-sms-steps` Inngest workflow, which
//   recomputes disabled_steps from `newPlan` (smsDisabledStepsForPlan) and
//   POSTs it to the PF business. Idempotent (writing the same disabled_steps is
//   a PF no-op).
// detailType in EventBridge: `marketing.plan-changed.v1` (Commitment 6).
//
// Status: active
//
// Why a SEPARATE event (not a field on marketing.subscription.ended.v1): that
// event is the marketing-ENDED (teardown) fact; this is the still-entitled
// plan-CHANGED fact with a different consumer action (re-sync steps, not tear
// down). Distinct facts, distinct async processes — EventBridge's default role
// (Commitment 6).

import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';
import { pocketflowBusinessIdSchema } from '../marketing/merchant-account.js';

export const marketingPlanChangedV1Schema = eventEnvelopeSchema.extend({
  // The PF business whose disabled_steps to re-sync. The org HAS one (the
  // producer only emits when the org entered Marketing), but the consumer
  // still no-ops + logs defensively if it ever arrives absent, per the
  // teardown/donation-bridge return-don't-throw precedent.
  pocketflowBusinessId: pocketflowBusinessIdSchema,
  // The org's NEW plan slug (e.g. 'PLUS'). The consumer derives disabled_steps
  // from it (smsDisabledStepsForPlan) so the SMS-inclusion derivation stays in
  // ONE place (marketing), matching enable-marketing + request-sms-number.
  // Kept as a loose non-empty string (NOT coupled to the Plan enum) so a future
  // tier needs no schema bump; the consumer's derivation already fails closed
  // (hides SMS) on an unrecognized plan.
  newPlan: z.string().min(1, { message: 'newPlan must be non-empty' }).max(64),
});

export type MarketingPlanChangedV1 = z.infer<typeof marketingPlanChangedV1Schema>;

export const MARKETING_PLAN_CHANGED_V1_DETAIL_TYPE =
  'marketing.plan-changed.v1' as const;
